import { useContext } from 'react';
import { useConfig } from '../Config/ConfigProvider';
import { ViewPortSizes } from '../Breakpoint/BreakpointProvider';
import { replaceCSToCDN, getContentStackURL, getQuality, getNextImageSizes } from './NextgenImage';
import FallbackNextgenImage from './FallbackNextgenImage';
import { NextgenImageContext, NextgenImageProvider } from './NextgenImageProvider';
import { type NextgenImageSrcObject, LoaderType, NextgenImageProps, ImageSourceType } from './types/propsTypes';

const NextgenImageV2 = ({
  fill = true,
  quality = 100,
  // TODO: Make CONTENTSTACK value default after PODs make their changes
  loaderType,
  isRelativeURL = false,
  fallbackQuality = 100,
  format = 'avif',
  src,
  priority,
  forceCdnUrlConversion = false,
  imageWidths,
  ...restProps
}: NextgenImageProps) => {
  const { element: NextJsImage, useCDNForCS, getImageProps } = useContext(NextgenImageContext);
  const configs = useConfig();
  const csImageUrl = configs.NEXT_PUBLIC_CONTENT_STACK_IMAGES_URL as string;
  const csCdnImageUrl = configs.NEXT_PUBLIC_CONTENT_STACK_CDN_URL as string;
  const CDNPostFix = configs.NEXT_PUBLIC_CONTENT_STACK_CDN_POSTFIX as string;
  const CDN_URL = `${configs.DEFAULT_HOST_URL}${CDNPostFix}`;
  const hasMultipleSources = typeof src === 'object' && (src.mobile || src.tablet || src.desktop);

  const getUpdatedSrc = (originalSrc?: string) =>
    replaceCSToCDN(originalSrc as string, useCDNForCS, csImageUrl, CDN_URL, csCdnImageUrl, forceCdnUrlConversion);
  // Get updated URLs for all sources
  let updatedMobileSRC: string;
  let updatedTabletSRC: string;
  let updatedDesktopSRC: string;
  if (hasMultipleSources) {
    const srcObj = src as NextgenImageSrcObject;
    updatedMobileSRC = getUpdatedSrc(srcObj.mobile);
    updatedTabletSRC = getUpdatedSrc(srcObj.tablet);
    updatedDesktopSRC = getUpdatedSrc(srcObj.desktop);
  } else {
    // Single source mode
    const singleSrc = typeof src === 'string' ? src : '';
    updatedMobileSRC = getUpdatedSrc(singleSrc);
    updatedTabletSRC = updatedMobileSRC;
    updatedDesktopSRC = updatedMobileSRC;
  }

  const customLoader = ({ src: loaderSrc, width }: { src: string; width: number }) => {
    if (loaderType === LoaderType.CONTENTSTACK) {
      return getContentStackURL(
        loaderSrc,
        useCDNForCS,
        CDN_URL,
        csImageUrl,
        width,
        format,
        quality,
        fallbackQuality,
        isRelativeURL,
        csCdnImageUrl,
        forceCdnUrlConversion,
      );
    } else if (loaderType === LoaderType.WEBFONTS && isRelativeURL) {
      return `${configs.NEXT_ASSETS_BASE_PATH}${loaderSrc}`;
    }

    return loaderSrc;
  };
  const noFilledImage = restProps.height && restProps.width;
  const generateImageProps = (imageSrc: string) => {
    if (!NextJsImage) {
      return null;
    }

    return (
      getImageProps &&
      getImageProps({
        src: imageSrc,
        loader: customLoader,
        ...getQuality(loaderType, quality),
        fill: noFilledImage ? false : fill,
        ...restProps,
        sizes: restProps.sizes ?? getNextImageSizes(restProps.height, restProps.width, imageWidths, fill),
      })
    );
  };

  const renderNextImage = NextJsImage && (hasMultipleSources ? updatedMobileSRC : updatedDesktopSRC);
  if (!renderNextImage) {
    return <FallbackNextgenImage src={updatedDesktopSRC} {...restProps} />; //Expecting single src for fallback
  }
  // Multi-source rendering
  if (hasMultipleSources) {
    const tabletImageProps = generateImageProps(updatedTabletSRC);
    const desktopImageProps = generateImageProps(updatedDesktopSRC);
    return (
      <picture>
        {/* Desktop source */}
        <source
          media={`(min-width: ${ViewPortSizes.LARGE}px)`}
          srcSet={desktopImageProps?.props.srcSet}
          type={`image/${format}`}
        />
        {/* Tablet source */}
        <source
          media={`(min-width: ${ViewPortSizes.MEDIUM}px)`}
          srcSet={tabletImageProps?.props.srcSet}
          type={`image/${format}`}
        />
        {/* Mobile image (fallback) */}
        <NextJsImage
          src={updatedMobileSRC}
          loader={customLoader}
          {...getQuality(loaderType, quality)}
          fill={noFilledImage ? false : fill}
          priority={priority}
          {...restProps}
          sizes={restProps.sizes ?? getNextImageSizes(restProps.height, restProps.width, imageWidths, fill)}
        />
      </picture>
    );
  }
  // Single source rendering (existing behavior)
  return (
    <NextJsImage
      src={updatedDesktopSRC}
      loader={customLoader}
      {...getQuality(loaderType, quality)}
      fill={noFilledImage ? false : fill}
      priority={priority}
      {...restProps}
      sizes={restProps.sizes ?? getNextImageSizes(restProps.height, restProps.width, imageWidths, fill)}
    />
  );
};

export { replaceCSToCDN, getContentStackURL, NextgenImageV2, NextgenImageProvider, LoaderType, ImageSourceType };
export type { NextgenImageProps, NextgenImageSrcObject };
